import { z } from "zod";
import { wechatSecretReferenceSchema } from "../m1-app";
const id = z.string().trim().min(1).max(128);
const key = z
  .string()
  .trim()
  .regex(/^[a-zA-Z0-9_.:-]{1,64}$/);
const units = z.number().int().min(1).max(1_000_000_000);
export const assetKindSchema = z.enum(["membership", "points", "item"]);
export const membershipQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).max(100000).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
    poolId: id.optional(),
    appId: id.optional(),
    userId: id.optional(),
    keyword: z.string().max(100).optional(),
    referenceId: id.optional(),
  })
  .strict();
export const purchaseQuerySchema = membershipQuerySchema.omit({ referenceId: true }).extend({
  payment: z.enum(["wechat_virtual", "points", "wxpay"]).optional(),
  status: z.enum(["pending", "fulfilled", "partially_refunded", "refunded", "canceled"]).optional(),
  env: z.coerce.number().int().min(0).max(1).optional(),
});
export type PurchaseQuery = z.input<typeof purchaseQuerySchema>;
const mutation = {
  mutationId: z.uuid(),
  reason: z.string().trim().min(2).max(500),
};
export const assetPoolInputSchema = z
  .object({
    ...mutation,
    name: z.string().trim().min(1).max(80),
    nameUg: z.string().trim().min(1).max(80),
    kind: assetKindSchema,
    enabled: z.boolean().default(true),
    sandbox: z.boolean().default(false),
    expectedVersion: z.number().int().min(0).default(0),
  })
  .strict();
export const assetBindingInputSchema = z
  .object({
    ...mutation,
    appId: id,
    kind: assetKindSchema,
    poolId: z.uuid().nullable(),
    expectedVersion: z.number().int().min(0),
    acknowledgeScopeChange: z.boolean(),
  })
  .strict();
export const assetProductInputSchema = z
  .object({
    ...mutation,
    poolId: z.uuid(),
    name: z.string().trim().min(1).max(100),
    nameUg: z.string().trim().max(100).optional(),
    assetKey: key,
    kind: assetKindSchema,
    priceMinor: units,
    pointsPrice: units.nullable().default(null),
    quantity: units,
    durationDays: z.number().int().min(1).max(36500).nullable(),
    level: z.number().int().min(1).max(100).default(1),
    permanent: z.boolean().default(false),
    consumable: z.boolean().default(true),
    benefits: z.array(key).max(50).default([]),
    status: z.enum(["draft", "published", "disabled"]),
    expectedVersion: z.number().int().min(0).default(0),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (
      v.kind === "membership" &&
      (v.quantity !== 1 || (!v.permanent && v.durationDays === null))
    )
      ctx.addIssue({
        code: "custom",
        message: "会员商品数量须为 1，非永久会员必须设置有效天数",
      });
    if (v.kind !== "membership" && v.permanent)
      ctx.addIssue({ code: "custom", message: "永久会员选项仅适用于会员" });
    if (
      v.kind === "points" &&
      (v.assetKey !== "points" || !v.consumable || v.pointsPrice !== null)
    )
      ctx.addIssue({
        code: "custom",
        message: "积分标识为 points，积分充值不支持积分支付",
      });
    if (v.kind === "item" && !v.consumable && v.quantity !== 1)
      ctx.addIssue({ code: "custom", message: "永久持有型道具数量须为 1" });
  });
export const assetCheckoutSchema = z
  .object({
    productId: z.uuid(),
    payment: z.enum(["wxpay", "points"]),
    platform: z.enum(["web", "android", "ios", "miniprogram"]),
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export const assetSpendSchema = z
  .object({
    kind: z.enum(["points", "item"]),
    assetKey: key,
    quantity: units,
    idempotencyKey: z.string().min(8).max(128),
    reason: z.string().trim().min(2).max(200),
  })
  .strict();
export const assetReverseSchema = z
  .object({
    operationId: z.uuid(),
    idempotencyKey: z.string().min(8).max(128),
    reason: z.string().trim().min(2).max(200),
  })
  .strict();
export const assetAdjustmentSchema = z
  .object({
    ...mutation,
    appId: id,
    userId: id,
    poolId: z.uuid(),
    assetKey: key,
    action: z.enum(["grant", "debit", "freeze", "unfreeze", "revoke"]),
    quantity: units.default(1),
    durationDays: z.number().int().min(1).max(36500).nullable().default(null),
    level: z.number().int().min(1).max(100).default(1),
    permanent: z.boolean().default(false),
    consumable: z.boolean().default(true),
    benefits: z.array(key).max(50).default([]),
  })
  .strict();
export const assetPoolSchema = z.object({
  sandbox: z.boolean().optional(),
  id: z.uuid(),
  name: z.string(),
  nameUg: z.string().optional(),
  kind: assetKindSchema,
  enabled: z.boolean(),
  version: z.number().int(),
  createdAt: z.string(),
});
export const assetBindingSchema = z.object({
  sandbox: z.boolean().optional(),
  appId: z.string(),
  appName: z.string(),
  kind: assetKindSchema,
  poolId: z.uuid().nullable(),
  poolName: z.string().nullable(),
  poolNameUg: z.string().nullable().optional(),
  version: z.number().int(),
});
export const assetProductSchema = z.object({
  id: z.uuid(),
  poolId: z.uuid(),
  name: z.string(),
  nameUg: z.string().optional(),
  assetKey: z.string(),
  virtualProductId: z.string().regex(/^[a-zA-Z0-9_.-]{1,64}$/).optional(),
  kind: assetKindSchema,
  priceMinor: z.number(),
  pointsPrice: z.number().nullable(),
  quantity: z.number().int().safe(),
  durationDays: z.number().nullable(),
  level: z.number(),
  permanent: z.boolean(),
  consumable: z.boolean(),
  benefits: z.array(z.string()),
  status: z.enum(["draft", "published", "disabled"]),
  version: z.number(),
  createdAt: z.string(),
});
export const assetAccountSchema = z.object({
  sandbox: z.boolean().optional(),
  poolId: z.uuid(),
  poolName: z.string(),
  poolNameUg: z.string().optional(),
  kind: assetKindSchema,
  userId: z.string(),
  userName: z.string(),
  userPublicId: z.string().regex(/^\d{1,20}$/).nullable().optional(),
  userAvatarUrl: z.union([
    z.literal("/open/user/avatar"),
    z.string().regex(/^https:\/\//).max(500),
    z.null(),
  ]).optional(),
  userDisplayName: z.string().max(80).optional(),
  assetKey: z.string(),
  available: z.number().int().safe(),
  debt: z.number().int().safe(),
  frozen: z.boolean(),
  level: z.number(),
  active: z.boolean(),
  expiresAt: z.string().nullable(),
  permanent: z.boolean(),
  benefits: z.array(z.string()),
});
export const assetPurchaseSchema = z.object({
  virtualPayment: z
    .object({
      outTradeNo: z.string(),
      env: z.number().int(),
      delivered: z.boolean(),
      lastError: z.string().nullable(),
      pendingRefunds: z.number().int(),
    })
    .nullable()
    .optional(),
  id: z.uuid(),
  appId: z.string(),
  userId: z.string(),
  originalUserId: z.string(),
  poolId: z.uuid(),
  productId: z.uuid(),
  productName: z.string(),
  productNameUg: z.string().optional(),
  appName: z.string().max(80).optional(),
  userDisplayName: z.string().max(80).optional(),
  userPublicId: z.string().regex(/^\d{1,20}$/).optional(),
  userAvatarUrl: z.union([
    z.literal("/open/user/avatar"),
    z.string().regex(/^https:\/\//).max(500),
    z.null(),
  ]).optional(),
  kind: assetKindSchema,
  payment: z.string(),
  orderId: z.string().nullable(),
  amount: z.number().int().safe(),
  status: z.enum([
    "pending",
    "fulfilled",
    "partially_refunded",
    "refunded",
    "canceled",
  ]),
  refundedAmount: z.number().int().safe(),
  createdAt: z.string(),
});
export const assetLedgerSchema = z.object({
  id: z.uuid(),
  poolId: z.uuid(),
  userId: z.string(),
  userDisplayName: z.string().max(80).optional(),
  userPublicId: z.string().regex(/^\d{1,20}$/).nullable().optional(),
  userAvatarUrl: z.union([
    z.literal("/open/user/avatar"),
    z.string().regex(/^https:\/\//).max(500),
    z.null(),
  ]).optional(),
  appId: z.string(),
  appName: z.string().max(80).optional(),
  assetKey: z.string(),
  action: z.string(),
  quantity: z.number().int().safe(),
  reason: z.string(),
  referenceId: z.string().nullable(),
  actor: z.string(),
  createdAt: z.string(),
});
export const assetAuditSchema = z.object({
  id: z.uuid(),
  action: z.string(),
  actor: z.string(),
  targetId: z.string(),
  reason: z.string(),
  createdAt: z.string(),
});
export const assetOperationSchema = z.object({
  operationId: z.uuid(),
  quantity: z.number().int().safe(),
  reversed: z.boolean(),
});
export const assetPageSchema = <T extends z.ZodType>(item: T) =>
  z.object({ items: z.array(item), total: z.number().int().nonnegative() });
export const membershipStateSchema = z.object({
  accounts: z.array(assetAccountSchema),
  bindings: z.array(assetBindingSchema),
});
export type AssetKind = z.infer<typeof assetKindSchema>;
export type AssetPool = z.infer<typeof assetPoolSchema>;
export type AssetBinding = z.infer<typeof assetBindingSchema>;
export type AssetProduct = z.infer<typeof assetProductSchema>;
export type AssetAccount = z.infer<typeof assetAccountSchema>;
export type AssetPurchase = z.infer<typeof assetPurchaseSchema>;
export type AssetLedger = z.infer<typeof assetLedgerSchema>;
export type AssetQuery = z.input<typeof membershipQuerySchema>;
export type AssetPoolInput = z.input<typeof assetPoolInputSchema>;
export type AssetProductInput = z.input<typeof assetProductInputSchema>;
export type AssetBindingInput = z.input<typeof assetBindingInputSchema>;
export type AssetAdjustment = z.input<typeof assetAdjustmentSchema>;

// AppSecret reuses the mini-program credential. AppKey and message-push secrets
// may be pasted in the admin page; only a reference is stored and returned.
const virtualStoredRef = z.string().regex(/^(env:\/\/[A-Z][A-Z0-9_]{2,100}|stored:\/\/virtual\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/);
const appKeyValue = z.string().regex(/^[A-Za-z0-9]{16,128}$/);
const callbackTokenValue = z.string().regex(/^[A-Za-z0-9]{3,32}$/);
const callbackAesValue = z.string().regex(/^[A-Za-z0-9]{43}$/);
export const virtualConfigInputSchema = z
  .object({
    ...mutation,
    appId: id,
    wxAppId: z.string().regex(/^wx[a-fA-F0-9]{16}$/),
    offerId: z.string().regex(/^\d{1,30}$/),
    env: z.union([z.literal(0), z.literal(1)]),
    appSecretRef: wechatSecretReferenceSchema,
    appKeyRef: virtualStoredRef.optional(),
    callbackTokenRef: virtualStoredRef.optional(),
    callbackAesKeyRef: virtualStoredRef.optional(),
    appKey: appKeyValue.optional(),
    callbackToken: callbackTokenValue.optional(),
    callbackAesKey: callbackAesValue.optional(),
    enabled: z.boolean(),
    expectedVersion: z.number().int().nonnegative(),
  })
  .strict();
export const virtualConfigSchema = virtualConfigInputSchema
  .omit({ mutationId: true, reason: true, expectedVersion: true, appKey: true, callbackToken: true, callbackAesKey: true })
  .extend({
    appKeyRef: virtualStoredRef,
    callbackTokenRef: virtualStoredRef,
    callbackAesKeyRef: virtualStoredRef,
    version: z.number().int(),
  })
  .strip();
export const virtualMappingInputSchema = z
  .object({
    ...mutation,
    appId: id,
    productId: z.uuid(),
    providerProductId: z.string().regex(/^[a-zA-Z0-9_.-]{1,64}$/),
    enabled: z.boolean(),
    expectedVersion: z.number().int().nonnegative(),
  })
  .strict();
export const virtualMappingSchema = virtualMappingInputSchema
  .omit({ mutationId: true, reason: true, expectedVersion: true })
  .extend({ version: z.number().int() })
  .strip();
const wechatProductId = z.string().regex(/^[a-zA-Z0-9_.-]{1,64}$/);
const subscribeContractCode = z.string().regex(/^[A-Za-z0-9]{1,32}$/);
const subscribeAccountName = z.string().trim().min(1).max(64).regex(/^[^()'"<>]+$/);
export const virtualSignContractSchema = z
  .object({
    productId: wechatProductId,
    outContractCode: subscribeContractCode,
    contractAccountName: subscribeAccountName,
  })
  .strict();
export const virtualPrepareInputSchema = z
  .object({
    productId: z.uuid(),
    idempotencyKey: z.string().min(8).max(128),
    loginCode: z.string().min(1).max(256),
    signContract: virtualSignContractSchema.optional(),
  })
  .strict();
const subscribeOrderId = z.string().regex(/^(?!_)[A-Za-z0-9_\-|\\*@]{8,32}$/);
export const virtualSubscribeActionSchema = z
  .object({
    action: z.enum(["query", "prepay", "charge", "cancel"]),
    appId: id,
    openid: z.string().min(1).max(128),
    productId: wechatProductId,
    mode: z.enum(["android", "apple"]).default("android"),
    outContractCode: subscribeContractCode.optional(),
    deductPrice: z.number().int().min(1).max(500_000).optional(),
    orderId: subscribeOrderId.optional(),
    attach: z.string().min(1).max(200).optional(),
    terminationReason: z.string().trim().min(1).max(200).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const needContract = value.action === "prepay" || value.action === "cancel" || (value.action === "query" && value.mode !== "apple");
    if (needContract && !value.outContractCode) ctx.addIssue({ code: "custom", message: "请填写签约协议号" });
    if (value.action === "prepay" && (value.deductPrice === undefined || value.deductPrice < 100)) ctx.addIssue({ code: "custom", message: "预通知金额至少 100 分" });
    if (value.action === "charge" && (value.deductPrice === undefined || !value.orderId || !value.attach)) ctx.addIssue({ code: "custom", message: "扣款需要金额、订单号和透传数据" });
    if (value.action === "cancel" && !value.terminationReason) ctx.addIssue({ code: "custom", message: "请填写解约原因" });
  });
export const virtualSubscribeQueryResultSchema = z.object({
  authorizationState: z.enum(["SIGNED", "TERMINATED", "UNBINDUSER"]),
  appleSubscriptionInfo: z.record(z.string(), z.unknown()).nullable(),
});
export const virtualSubscribeAcceptedSchema = z.object({ accepted: z.literal(true) });
export const virtualSubscribeResultSchema = z.union([virtualSubscribeQueryResultSchema, virtualSubscribeAcceptedSchema]);
export const virtualSubscribeSignInputSchema = z
  .object({
    loginCode: z.string().min(1).max(256),
    scene: z.enum(["android", "apple"]),
    productId: wechatProductId,
    outContractCode: subscribeContractCode.optional(),
    contractAccountName: subscribeAccountName.optional(),
    goodsPrice: z.number().int().min(100).max(500_000).optional(),
    activitySellingPrice: z.number().int().min(0).max(500_000).optional(),
    attach: z.string().min(1).max(200).refine((value) => !/[\u0000-\u001f<>]/.test(value)).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.scene === "android" && (!value.outContractCode || !value.contractAccountName)) ctx.addIssue({ code: "custom", message: "安卓签约需要协议号和展示名称" });
    if (value.scene === "apple" && (value.goodsPrice === undefined || !value.attach)) ctx.addIssue({ code: "custom", message: "苹果签约需要价格和透传数据" });
  });
export const virtualSubscribeSignResultSchema = z.object({
  api: z.enum(["requestSubscribeSign", "requestAppleSubscribeSign"]),
  signData: z.string(),
  paySig: z.string(),
  signature: z.string(),
  openid: z.string(),
});
export const virtualSubscribeUserQuerySchema = z
  .object({
    loginCode: z.string().min(1).max(256),
    productId: wechatProductId,
    mode: z.enum(["android", "apple"]).default("android"),
    outContractCode: subscribeContractCode.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.mode !== "apple" && !value.outContractCode) ctx.addIssue({ code: "custom", message: "安卓签约查询必须填写协议号" });
  });
export const virtualSyncInputSchema = z
  .object({ purchaseId: z.uuid() })
  .strict();
export const virtualRefundInputSchema = z
  .object({
    ...mutation,
    amountMinor: units,
    refundReason: z.enum(["0", "1", "2", "3", "4", "5"]).default("3"),
  })
  .strict();
export const virtualPaymentParamsSchema = z.object({
  mode: z.literal("short_series_goods"),
  signData: z.string(),
  paySig: z.string(),
  signature: z.string(),
});
export const virtualPrepareResultSchema = z.object({
  purchase: assetPurchaseSchema,
  payment: virtualPaymentParamsSchema.nullable(),
});
export const virtualSyncResultSchema = z.object({
  purchase: assetPurchaseSchema,
  deliveryPending: z.boolean(),
  providerStatus: z.number().int(),
});
export const virtualRefundSchema = z.object({
  id: z.string(),
  purchaseId: z.uuid(),
  amountMinor: z.number().int(),
  status: z.enum(["pending", "succeeded", "failed"]),
  reason: z.string(),
  createdAt: z.string(),
});
export type VirtualConfig = z.infer<typeof virtualConfigSchema>;
export type VirtualMapping = z.infer<typeof virtualMappingSchema>;

// Administrator-only purchase snapshot and trace. Refund requests are paged;
// ledger preview is bounded and links to the exact-reference ledger query.
export const purchaseTraceQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
}).strict();
export const purchaseTraceSchema = z.object({
  purchase: assetPurchaseSchema,
  appName: z.string(), poolName: z.string(), userName: z.string(), originalUserName: z.string(),
  userPublicId: z.string().regex(/^\d{1,20}$/).nullable().optional(),
  originalUserPublicId: z.string().regex(/^\d{1,20}$/).nullable().optional(),
  userAvatarUrl: z.union([z.literal("/open/user/avatar"), z.string().regex(/^https:\/\//).max(500), z.null()]).optional(),
  originalUserAvatarUrl: z.union([z.literal("/open/user/avatar"), z.string().regex(/^https:\/\//).max(500), z.null()]).optional(),
  snapshot: assetProductSchema.omit({ id: true, poolId: true, status: true, version: true, createdAt: true }).extend({ priceMinor: z.number().nullable(), pointsPrice: z.number().nullable() }),
  lot: z.object({
    id: z.uuid(), userId: z.string(), poolId: z.uuid(), assetKey: z.string(),
    quantity: z.number().int().safe(), remaining: z.number().int().safe(), reclaimed: z.number().int().safe(), expired: z.number().int().safe(),
    revoked: z.boolean(), startsAt: z.string(), expiresAt: z.string().nullable(),
  }).nullable(),
  ledger: assetPageSchema(assetLedgerSchema),
  refunds: assetPageSchema(virtualRefundSchema),
});
export type PurchaseTrace = z.infer<typeof purchaseTraceSchema>;
export type PurchaseTraceQuery = z.input<typeof purchaseTraceQuerySchema>;
